import React from "react";
import { gql, useQuery } from "@apollo/client";
import {
  ActivitySortField,
  SortOrder,
  VistActivityItem as Activity,
} from "../../generated/graphql";
import { KeycloakAccessToken } from "../../lib/keycloakAccessToken";
import { useRefreshError } from "../../lib/useRefreshError";
import { Section } from "../Page/Section";
import { StyledLink, Text } from "../shared/typography";
import Button from "../shared/Button";
import { VistActivityItem } from "./VistActivityItem";

export const ActivityPreview = ({ user }: { user?: KeycloakAccessToken }) => {
  const query = useQuery<{ vistActivities: Activity[] }>(
    gql`
      query FetchRecentVistActivities(
        $groups: [Int!]!
        $searchParams: ActivitySearchParams
        $limit: Int
      ) {
        vistActivities(
          groups: $groups
          searchParams: $searchParams
          limit: $limit
        ) {
          id
          activityStart
          createdBy {
            first_name
            last_name
            email
          }
          unitName
          courseName
          totalTimeInSeconds
          result
          progress
          exerciseName
          moduleName
        }
      }
    `,
    {
      skip: !user,
      variables: {
        groups: [],
        searchParams: {
          sortField: ActivitySortField.ActivityStart,
          sortOrder: SortOrder.Desc,
          searchText: "",
        },
        limit: 3,
      },
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
    }
  );
  const error = useRefreshError(query, user?.sub || "");
  const activities = query.data?.vistActivities;
  return (
    <Section
      title="Activity log"
      maxColumns={1}
      primaryActionText="View All"
      primaryActionLink="/activity-log"
    >
      {error && (
        <div role="alert">
          <Text>
            {activities?.length
              ? "Activities could not be refreshed. Showing the last loaded activities."
              : "Activities could not be loaded."}
          </Text>
          <Button
            secondary
            disabled={query.loading}
            onClick={() => {
              void query.refetch().catch(() => undefined);
            }}
          >
            Retry
          </Button>
        </div>
      )}
      {(query.loading || !user) && activities === undefined ? (
        <div role="status">
          <Text>Loading activities...</Text>
        </div>
      ) : activities?.length ? (
        activities.map((activity) => (
          <StyledLink
            to={`/activity-log/vist-activity/${activity.id}`}
            key={activity.id}
          >
            <VistActivityItem activity={activity} />
          </StyledLink>
        ))
      ) : !error ? (
        <Text>No activities found.</Text>
      ) : null}
    </Section>
  );
};
